import React, { FC, useEffect, useMemo, useState } from 'react';
import Modal from '@mui/material/Modal';
import dayjs, { Dayjs } from 'dayjs';
import useSnackbarHelper from '../../../../hooks/snackbar.hook';
import { computeStartDatePreview, getAnnualAnchorInCurrentYear } from '../../../../helpers/datetime.helpers';
import { useTrainingLogContext } from '../../training-log.context';
import './start-date-modal.component.css';

interface StartDateModalProps {
  open: boolean;
  onClose: () => void;
}

const CalendarInputIcon: FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const StartDateModal: FC<StartDateModalProps> = ({ open, onClose }) => {
  const { annualAnchor, updateAnnualAnchor, annualAnchorSaving } = useTrainingLogContext();
  const { showSnackbar } = useSnackbarHelper();
  const [formDate, setFormDate] = useState<Dayjs | null>(null);

  useEffect(() => {
    if (open && annualAnchor) {
      setFormDate(getAnnualAnchorInCurrentYear(annualAnchor));
    }
  }, [open, annualAnchor]);

  const preview = useMemo(() => {
    if (!formDate) return null;
    return computeStartDatePreview(formDate);
  }, [formDate]);

  const handleDateChange = (value: string) => {
    if (!value) {
      setFormDate(null);
      return;
    }
    setFormDate(dayjs(value));
  };

  const handleSave = async () => {
    if (!formDate) {
      return;
    }

    try {
      await updateAnnualAnchor(formDate);
      showSnackbar('Successfully updated annual start date.', 'success');
      onClose();
    } catch {
      showSnackbar('An error occurred while updating your annual start date. Please try again later.', 'error');
    }
  };

  const handleCancel = () => {
    if (annualAnchor) {
      setFormDate(getAnnualAnchorInCurrentYear(annualAnchor));
    }
    onClose();
  };

  return (
    <Modal open={open} onClose={handleCancel} className="sd-modal-overlay">
      <div className="sd-modal">
        <div className="sd-modal-header">
          <div className="sd-modal-title">Set Training Start Date</div>
          <button type="button" className="sd-modal-close" onClick={handleCancel} aria-label="Close">
            ✕
          </button>
        </div>

        <p className="sd-modal-desc">
          Choose your annual start date. Your season begins on the Monday of the week containing this date. All periods,
          weeks, and logs are calculated from that week.
        </p>

        <div className="sd-date-input-wrap">
          <label className="sd-label" htmlFor="training-start-date">
            Start Date
          </label>
          <div className="sd-date-input-field">
            <input
              id="training-start-date"
              type="date"
              className="sd-date-input"
              value={formDate ? formDate.format('YYYY-MM-DD') : ''}
              onChange={(e) => handleDateChange(e.target.value)}
              disabled={annualAnchorSaving}
            />
            <span className="sd-date-input-icon">
              <CalendarInputIcon />
            </span>
          </div>
        </div>

        {preview && (
          <div className="sd-modal-preview">
            <div className="sd-preview-item">
              <span className="sd-preview-label">Season</span>
              <span className="sd-preview-val">{preview.season}</span>
            </div>
            <div className="sd-preview-item">
              <span className="sd-preview-label">Week 1 begins</span>
              <span className="sd-preview-val">{preview.week1Begins}</span>
            </div>
            <div className="sd-preview-item">
              <span className="sd-preview-label">Period 1</span>
              <span className="sd-preview-val">{preview.period1}</span>
            </div>
            <div className="sd-preview-item">
              <span className="sd-preview-label">Season end (52 wks)</span>
              <span className="sd-preview-val">{preview.seasonEnd}</span>
            </div>
          </div>
        )}

        <div className="sd-modal-actions">
          <button type="button" className="sd-btn-cancel" onClick={handleCancel} disabled={annualAnchorSaving}>
            Cancel
          </button>
          <button
            type="button"
            className="sd-btn-apply"
            onClick={handleSave}
            disabled={annualAnchorSaving || !formDate}
          >
            Apply Start Date
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default StartDateModal;
